import { InfoCircleTwoTone, LoadingOutlined } from "@ant-design/icons";
import { InventoryItem as InventoryItemType } from "@p0security/shared/assessment/inventory";
import { Col, Row, Spin, Typography } from "antd";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { GraphTooltip } from "components/GraphTooltip";
import { groupBy, mapValues } from "lodash";
import { useContext, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { ScopeContext } from "../contexts/ScopeContext";
import { useControls } from "../hooks/useControls";
import { makeKey, useInventoryCounts } from "../hooks/useInventoryCounts";
import { transformInventoryData } from "../utils/inventory";

const InventoryItemDiv = styled.div`
  &:not(:first-child) {
    margin-left: 16px;
  }
`;

const MAX_PER_CATEGORY = 10;

const HEADING_TO_SPAN: Record<string, number> = {
  resource: 7,
  grant: 5,
  identity: 7,
  credential: 5,
};

const InventoryItem: React.FC<{
  link: InventoryItemType<any>;
  count: number | undefined;
}> = ({ link, count }) => {
  const { controls } = useControls();
  const { provider } = useContext(ScopeContext);
  const [search] = useSearchParams();

  const url = useMemo(() => {
    const params = new URLSearchParams(search);
    params.set("show", link.show);
    params.set("where", link.where);
    return `?${params.toString()}`;
  }, [link, search]);

  const isCurrent =
    controls.show === link.show && controls.where === link.where;
  const isResourceAll = link.show === "resource" && provider === "all";
  const displayCount =
    !isResourceAll && count !== undefined ? `(${count})` : null;

  return (
    <InventoryItemDiv>
      {count && !isCurrent ? (
        <Link to={url}>{link.label}</Link>
      ) : (
        <Typography.Text
          type="secondary"
          style={isCurrent ? { fontWeight: "bold" } : {}}
        >
          {link.label}
        </Typography.Text>
      )}
      {link.tooltip && (
        <>
          <span>&nbsp;</span>
          <GraphTooltip title={link.tooltip}>
            <InfoCircleTwoTone />
          </GraphTooltip>
        </>
      )}
      &nbsp;
      {count === undefined ? (
        <Spin indicator={<LoadingOutlined spin />} size="small" />
      ) : (
        <Typography.Text type="secondary">{displayCount}</Typography.Text>
      )}
    </InventoryItemDiv>
  );
};

export const Inventory: React.FC = () => {
  const { assessment } = useContext(SelectedEnvironmentContext);
  const { graph, provider } = useContext(ScopeContext);

  const allLinks = useMemo(() => {
    if (!assessment.doc) return [];
    return transformInventoryData(assessment.doc.data.targets, provider);
  }, [assessment.doc, provider]);

  const { counts } = useInventoryCounts(allLinks, graph);

  const sorted = useMemo(() => {
    if (!allLinks.length) return {};
    const groups = groupBy(allLinks, "category");
    return mapValues(groups, (links) =>
      links
        .sort((a, b) => (counts[makeKey(b)] ?? 0) - (counts[makeKey(a)] ?? 0))
        .slice(0, MAX_PER_CATEGORY)
    );
  }, [allLinks, counts]);

  if (!graph) return null;

  return (
    <Row style={{ maxWidth: 1400, width: "100%" }}>
      {Object.entries(sorted).map(([category, links]) => {
        const span = HEADING_TO_SPAN[category];
        return (
          <Col key={category} span={span}>
            {links.map((link) => (
              <InventoryItem
                key={makeKey(link)}
                link={link}
                count={counts[makeKey(link)]}
              />
            ))}
          </Col>
        );
      })}
    </Row>
  );
};
